import React, { useEffect, useMemo, useState } from "react";
import { US_STATES } from "../../integrations/usGeo/usStates";
import {
  LEGAL_LISTING_CATEGORIES,
  LEGAL_SOURCES,
  buildSourceLinks,
} from "../../integrations/legalListings/legalSources";

// Lightweight, no-auth hub UI:
// - keep existing category cards (publish flow)
// - add state + county selector
// - show "connectable" sources + links
// - optional: pull a small federal sample feed (no keys) to prove wiring

export default function LegalListingsHub({ t, onPublishCategory, publishPanel }) {
  const [activeCat, setActiveCat] = useState("public");
  const [stateFips, setStateFips] = useState("12"); // default Florida
  const [countyFips, setCountyFips] = useState("");
  const [counties, setCounties] = useState([]);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [countyError, setCountyError] = useState("");
  const [showDevLinks, setShowDevLinks] = useState(false);

const MANUAL_LINKS_KEY = "wow5050_manual_links_v1";
const MANUAL_LISTINGS_KEY = "wow5050_manual_listings_v1";
const [manualLabel, setManualLabel] = useState("");
const [manualUrl, setManualUrl] = useState("");
const [manualLinks, setManualLinks] = useState(() => {
  try {
    const raw = localStorage.getItem(MANUAL_LINKS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
});

// Manual listings (stored locally in the browser). This is the "manual add" section for notices.
const [draftCat, setDraftCat] = useState("public");
const [draftHeadline, setDraftHeadline] = useState("");
const [draftDescription, setDraftDescription] = useState("");
const [draftPrice, setDraftPrice] = useState("");
const [draftLocation, setDraftLocation] = useState("");
const [draftContactName, setDraftContactName] = useState("");
const [draftContactEmail, setDraftContactEmail] = useState("");
const [draftContactPhone, setDraftContactPhone] = useState("");
// Traffic-specific fields (optional)
const [draftCitationNo, setDraftCitationNo] = useState("");
const [draftTrafficStateFips, setDraftTrafficStateFips] = useState("12");
const [draftTrafficCounty, setDraftTrafficCounty] = useState("");

useEffect(() => {
  try {
    localStorage.setItem(MANUAL_LINKS_KEY, JSON.stringify(manualLinks));
  } catch {
    // ignore
  }
}, [manualLinks]);

const addManualLink = () => {
  const label = manualLabel.trim();
  const urlRaw = manualUrl.trim();
  if (!label || !urlRaw) return;
  const href = /^https?:\/\//i.test(urlRaw) ? urlRaw : `https://${urlRaw}`;
  setManualLinks((prev) => {
    const next = [
      { id: `${Date.now()}-${Math.random().toString(16).slice(2)}`, label, href, createdAt: new Date().toISOString() },
      ...(Array.isArray(prev) ? prev : []),
    ];
    return next.slice(0, 100);
  });
  setManualLabel("");
  setManualUrl("");
};

const removeManualLink = (id) => {
  setManualLinks((prev) => (Array.isArray(prev) ? prev.filter((x) => x.id !== id) : []));
};

// Manual listings (drafts) that the user can add directly on the page.
const [mCat, setMCat] = useState("public");
const [mHeadline, setMHeadline] = useState("");
const [mDescription, setMDescription] = useState("");
const [mPrice, setMPrice] = useState("");
const [mLocation, setMLocation] = useState("");
const [mContactName, setMContactName] = useState("");
const [mContactEmail, setMContactEmail] = useState("");
const [mContactPhone, setMContactPhone] = useState("");
const [mTrafficCitation, setMTrafficCitation] = useState("");
const [mTrafficPortal, setMTrafficPortal] = useState("");

  const [mError, setMError] = useState("");
const [manualListings, setManualListings] = useState(() => {
  try {
    const raw = localStorage.getItem(MANUAL_LISTINGS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
});

useEffect(() => {
  try {
    localStorage.setItem(MANUAL_LISTINGS_KEY, JSON.stringify(manualListings));
  } catch {
    // ignore
  }
}, [manualListings]);

const addManualListing = () => {
  
    setMError("");
const headline = mHeadline.trim();
  const description = mDescription.trim();
  if (!headline || !description) return;

  const entry = {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    category: mCat,
    headline,
    description,
    price: (mPrice || "").trim(),
    location: (mLocation || "").trim(),
    contactName: (mContactName || "").trim(),
    contactEmail: (mContactEmail || "").trim(),
    contactPhone: (mContactPhone || "").trim(),
    trafficCitation: (mCat === "traffic" ? (mTrafficCitation || "").trim() : ""),
    trafficPortal: (mCat === "traffic" ? (mTrafficPortal || "").trim() : ""),
    stateFips,
    countyFips,
    createdAt: new Date().toISOString(),
  };

  setMError("");

    setManualListings((prev) => [entry, ...(Array.isArray(prev) ? prev : [])].slice(0, 200));
  setMHeadline("");
  setMDescription("");
  setMPrice("");
  setMLocation("");
  setMContactName("");
  setMContactEmail("");
  setMContactPhone("");
  setMTrafficCitation("");
  setMTrafficPortal("");
};

const removeManualListing = (id) => {
  setManualListings((prev) => (Array.isArray(prev) ? prev.filter((x) => x.id !== id) : []));
};

  const tabs = useMemo(() => {
    return [
      ...LEGAL_LISTING_CATEGORIES,
      {
        key: "traffic",
        title: "Traffic",
        description: "Traffic citation lookups (opens the official portal when known; otherwise opens a .gov-focused Google search).",
      },
    ];
  }, []);

  const activeTab = useMemo(() => {
    return tabs.find((c) => c.key === activeCat) || tabs[0];
  }, [tabs, activeCat]);

  const isTraffic = activeCat === "traffic";

  const stateObj = useMemo(
    () => US_STATES.find((s) => s.fips === stateFips) || US_STATES[0],
    [stateFips]
  );

  const selectedCounty = useMemo(
    () => counties.find((c) => c.fips === countyFips) || null,
    [counties, countyFips]
  );

  const trafficQuery = useMemo(() => {
  const stateName = stateObj?.name || stateObj?.abbr || "";
  const countyName =
    selectedCounty?.name && selectedCounty.name !== "All counties" ? selectedCounty.name : "";
  return ["traffic citation", countyName, stateName]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}, [stateObj, selectedCounty]);

const trafficGoogleUrl = useMemo(() => {
  const q = trafficQuery || "traffic citation";
  return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
}, [trafficQuery]);

const trafficPortalUrl = useMemo(() => {
  // Best-effort resolver:
  // 1) Known official portals (curated)
  // 2) Fallback to a .gov-focused Google search for the selected county/state
  const stateAbbr = (stateObj?.abbr || "").trim();
  const countyName = (selectedCounty?.name || "").trim();

  const overrides = {
    "FL|Miami-Dade County": "https://www2.miamidadeclerk.gov/trafficapp/allsearches.aspx",
    "FL|Miami Dade County": "https://www2.miamidadeclerk.gov/trafficapp/allsearches.aspx",
  };

  const override = overrides[`${stateAbbr}|${countyName}`];
  if (override) return override;

  const q = (trafficQuery ? `${trafficQuery} site:.gov` : "traffic citation site:.gov")
    .replace(/\s+/g, " ")
    .trim();

  return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
}, [stateObj, selectedCounty, trafficQuery]);

const openTrafficPortal = () => {
  try {
    window.open(trafficPortalUrl, "_blank", "noopener,noreferrer");
  } catch (e) {
    // no-op
  }
};

const openTrafficGoogle = () => {
  try {
    window.open(trafficGoogleUrl, "_blank", "noopener,noreferrer");
  } catch (e) {
    // no-op
  }
};


  const sourcesForCategory = useMemo(
    () => LEGAL_SOURCES.filter((s) => (s.categoryKeys || []).includes(activeCat)),
    [activeCat]
  );

  const sourceLinks = useMemo(() => {
    return buildSourceLinks({ state: stateObj, countyFips, categoryKey: activeCat });
  }, [stateObj, countyFips, activeCat]);

  const headingText = (t?.legalListings?.heading || "Legal Listings & Public Notices").trim();
  const fallbackSub =
    "Click a category to publish a listing (headline, description, contact info, and optional pricing/files).";
  const subTextRaw = (t?.legalListings?.sub || "").trim();
  // Some translations/configs accidentally set `sub` equal to the heading; avoid showing duplicate text.
  const subText = subTextRaw && subTextRaw !== headingText ? subTextRaw : fallbackSub;

  useEffect(() => {
    let cancelled = false;

    const FL_COUNTIES_FALLBACK = [
        { name: "Alachua County", fips: "001" },
        { name: "Baker County", fips: "003" },
        { name: "Bay County", fips: "005" },
        { name: "Bradford County", fips: "007" },
        { name: "Brevard County", fips: "009" },
        { name: "Broward County", fips: "011" },
        { name: "Calhoun County", fips: "013" },
        { name: "Charlotte County", fips: "015" },
        { name: "Citrus County", fips: "017" },
        { name: "Clay County", fips: "019" },
        { name: "Collier County", fips: "021" },
        { name: "Columbia County", fips: "023" },
        { name: "DeSoto County", fips: "027" },
        { name: "Dixie County", fips: "029" },
        { name: "Duval County", fips: "031" },
        { name: "Escambia County", fips: "033" },
        { name: "Flagler County", fips: "035" },
        { name: "Franklin County", fips: "037" },
        { name: "Gadsden County", fips: "039" },
        { name: "Gilchrist County", fips: "041" },
        { name: "Glades County", fips: "043" },
        { name: "Gulf County", fips: "045" },
        { name: "Hamilton County", fips: "047" },
        { name: "Hardee County", fips: "049" },
        { name: "Hendry County", fips: "051" },
        { name: "Hernando County", fips: "053" },
        { name: "Highlands County", fips: "055" },
        { name: "Hillsborough County", fips: "057" },
        { name: "Holmes County", fips: "059" },
        { name: "Indian River County", fips: "061" },
        { name: "Jackson County", fips: "063" },
        { name: "Jefferson County", fips: "065" },
        { name: "Lafayette County", fips: "067" },
        { name: "Lake County", fips: "069" },
        { name: "Lee County", fips: "071" },
        { name: "Leon County", fips: "073" },
        { name: "Levy County", fips: "075" },
        { name: "Liberty County", fips: "077" },
        { name: "Madison County", fips: "079" },
        { name: "Manatee County", fips: "081" },
        { name: "Marion County", fips: "083" },
        { name: "Martin County", fips: "085" },
        { name: "Miami-Dade County", fips: "086" },
        { name: "Monroe County", fips: "087" },
        { name: "Nassau County", fips: "089" },
        { name: "Okaloosa County", fips: "091" },
        { name: "Okeechobee County", fips: "093" },
        { name: "Orange County", fips: "095" },
        { name: "Osceola County", fips: "097" },
        { name: "Palm Beach County", fips: "099" },
        { name: "Pasco County", fips: "101" },
        { name: "Pinellas County", fips: "103" },
        { name: "Polk County", fips: "105" },
        { name: "Putnam County", fips: "107" },
        { name: "St. Johns County", fips: "109" },
        { name: "St. Lucie County", fips: "111" },
        { name: "Santa Rosa County", fips: "113" },
        { name: "Sarasota County", fips: "115" },
        { name: "Seminole County", fips: "117" },
        { name: "Sumter County", fips: "119" },
        { name: "Suwannee County", fips: "121" },
        { name: "Taylor County", fips: "123" },
        { name: "Union County", fips: "125" },
        { name: "Volusia County", fips: "127" },
        { name: "Wakulla County", fips: "129" },
        { name: "Walton County", fips: "131" },
        { name: "Washington County", fips: "133" }
    ];

    const normalizeList = (list) => {
      const byFips = new Map();
      for (const c of Array.isArray(list) ? list : []) {
        const name = String(c?.name || "").trim();
        const fips = String(c?.fips || "").trim().padStart(3, "0");
        if (!name || !/^[0-9]{3}$/.test(fips)) continue;
        byFips.set(fips, { name, fips });
      }
      return Array.from(byFips.values()).sort((a, b) => a.name.localeCompare(b.name));
    };

    const parseCensusRows = (rows) => {
      // Expected: [ ["NAME","state","county"], ["Miami-Dade County, Florida","12","086"], ... ]
      if (!Array.isArray(rows) || rows.length < 2) return [];
      return normalizeList(
        rows
          .slice(1)
          .map((row) => {
            const full = String(row?.[0] || "").trim();
            const name = full.replace(/,\s*[A-Za-z\s]+$/, ""); // strip ", Florida"
            const countyFips = String(row?.[2] || "").padStart(3, "0");
            return name ? { name, fips: countyFips } : null;
          })
          .filter(Boolean)
      );
    };

    async function fetchFromApi() {
      const res = await fetch(`/api/us-counties?state=${encodeURIComponent(stateFips)}`, {
        headers: { Accept: "application/json" },
      });

      // If you're running with `npm run dev`, /api routes are not served and you may get HTML back.
      const ct = String(res.headers.get("content-type") || "").toLowerCase();
      let json = null;

      try {
        json = await res.json();
      } catch {
        // If the API route is not available (common when a SPA rewrite catches /api),
        // do NOT hard-fail here—let the Census fallback handle it.
        await res.text().catch(() => "");
        return [];
      }

      if (!res.ok) throw new Error(`Failed to load counties (HTTP ${res.status}).`);
      if (json?.error) throw new Error(String(json.error));
      return normalizeList(Array.isArray(json?.counties) ? json.counties : []);
    }

    async function fetchFromCensus() {
      const url = `https://api.census.gov/data/2010/dec/sf1?get=NAME&for=county:*&in=state:${encodeURIComponent(
        stateFips
      )}`;
      const r = await fetch(url, { headers: { Accept: "application/json" } });
      if (!r.ok) throw new Error(`Census HTTP ${r.status}`);
      const rows = await r.json();
      return parseCensusRows(rows);
    }

    async function load() {
      setLoadingCounties(true);
      setCountyError("");

      try {
        let parsed = [];
        let lastErr = null;

        // 1) Prefer the server-side proxy (works on Vercel + `vercel dev`).
        try {
          parsed = await fetchFromApi();
        } catch (e) {
          lastErr = e;
        }

        // 2) Fallback to direct Census (works in plain `npm run dev` and as a backup).
        if (!parsed.length) {
          try {
            parsed = await fetchFromCensus();
            lastErr = null;
          } catch (e) {
            lastErr = lastErr || e;
          }
        }

        // 3) Final fallback for Florida so the dropdown still works even if the network blocks Census.
        if (!parsed.length && stateFips === "12") {
          parsed = normalizeList(FL_COUNTIES_FALLBACK);
        }

        if (!parsed.length && lastErr) throw lastErr;

        if (!cancelled) {
          setCounties(parsed);
          setCountyFips("");
        }
      } catch (e) {
        if (!cancelled) {
          const parsedFallback = stateFips === "12" ? normalizeList(FL_COUNTIES_FALLBACK) : [];
          setCounties(parsedFallback);
          setCountyFips("");
          // If we can still populate a usable list (Florida offline fallback), don't block the UI with an error.
          setCountyError(parsedFallback.length ? "" : e?.message || "Failed to load counties");
        }
      } finally {
        if (!cancelled) setLoadingCounties(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [stateFips]);

  const pillBtn =
    "px-4 py-2 rounded-full border border-sky-400/80 text-sm font-semibold hover:bg-sky-400/10 transition";
  const card =
    "rounded-2xl border border-sky-400/70 bg-slate-950/60 shadow-[0_0_0_1px_rgba(56,189,248,0.10)]";

  const sourceAccess = (s) => {
    if (s?.type === "api") return "API";
    if (s?.type === "directory") return "Directory";
    if (s?.type === "restricted") return "Restricted";
    return s?.type ? String(s.type) : "Unknown";
  };

  const sourceBadge = (s) => {
    if (s?.type === "restricted") return "Restricted";
    const note = (s?.note || "").toLowerCase();
    if (note.includes("api key")) return "API key";
    if (note.includes("requires")) return "Requires setup";
    if (s?.type === "directory") return "Directory";
    if (s?.type === "api") return "API";
    return "";
  };


  return (
    <div className="space-y-6">

      {/* category pills (publish flow) */}
      <div className={`${card} p-4 sm:p-6 space-y-4`}>
        <div className="flex flex-wrap gap-2">
          {tabs.map((c, idx) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setActiveCat(c.key)}
              className={`${pillBtn} ${activeCat === c.key ? "bg-sky-400/15" : ""}`}
              aria-pressed={activeCat === c.key}
            >
              {t?.legalListings?.items?.[idx] || c.title}
            </button>
          ))}
        </div>

        {/* category cards (publish) or Traffic tools */}
        {!isTraffic ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {LEGAL_LISTING_CATEGORIES.map((c, idx) => (
              <button
                key={c.key}
                type="button"
                onClick={() => onPublishCategory?.(c.key)}
                className={`${card} p-5 text-left hover:bg-slate-950/80 transition`}
              >
                <div className="text-xl font-semibold text-white">
                  {t?.legalListings?.items?.[idx] || c.title}
                </div>
                <div className="mt-2 text-slate-200/70 text-sm">
                  {t?.legalListings?.publishHint ||
                    "Publish: headline · description · contact · optional price/files"}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className={`${card} p-5 text-left`}>
            <div className="text-xl font-semibold text-white">Traffic citations</div>
            <div className="mt-2 text-slate-200/70 text-sm">
              Choose a state + county, then open Google with the search already typed. You can then click the official result for your county.
            </div>

            <div className="mt-4 flex flex-col md:flex-row md:items-start gap-4">
              <div className="flex-1">
                <div className="text-sm text-slate-200/70 mb-1">State</div>
                <select
                  className="w-full rounded-xl bg-slate-950/70 border border-sky-400/40 px-3 py-2"
                  value={stateFips}
                  onChange={(e) => setStateFips(e.target.value)}
                >
                  {US_STATES.map((s) => (
                    <option key={s.fips} value={s.fips}>
                      {s.name} ({s.abbr})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1">
                <div className="text-sm text-slate-200/70 mb-1">County</div>
                <select
                  className="w-full rounded-xl bg-slate-950/70 border border-sky-400/40 px-3 py-2"
                  value={countyFips}
                  onChange={(e) => setCountyFips(e.target.value)}
                  disabled={loadingCounties || counties.length === 0}
                >
                  <option value="">
                    {loadingCounties ? "Loading counties…" : "All counties"}
                  </option>
                  {counties.map((c) => (
                    <option key={c.fips} value={c.fips}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {countyError ? <div className="mt-2 text-xs text-rose-300">{countyError}</div> : null}
                <div className="mt-2 text-xs text-slate-200/60">
                  County list loads via a server proxy (and falls back to the U.S. Census API). If it fails, try disabling VPN/ad-blockers or run `vercel dev` in local development.
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={openTrafficPortal}
                className="text-xs px-3 py-1 rounded-full border border-emerald-400/30 hover:bg-emerald-400/10"
              >
                Traffic search
              </button>
              <button
                type="button"
                onClick={openTrafficGoogle}
                className="text-xs px-3 py-1 rounded-full border border-sky-400/30 hover:bg-sky-400/10"
              >
                Open Google results
              </button>
              <span className="text-xs text-slate-200/60">
                (We open the official county portal when we have it; otherwise we open a .gov-focused Google search for your county/state.)
              </span>
            </div>
          </div>
        )}
      </div>

      {publishPanel ? <div>{publishPanel}</div> : null}

      {!isTraffic && (
      <>
      {/* US-wide directory + connectable sources */}
      <div className={`${card} p-4 sm:p-6 space-y-4`}>
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div className="flex-1">
            <div className="text-sm text-slate-200/70 mb-1">{t?.legalListings?.selectState || "State"}</div>
            <select
              className="w-full rounded-xl bg-slate-950/70 border border-sky-400/40 px-3 py-2"
              value={stateFips}
              onChange={(e) => setStateFips(e.target.value)}
            >
              {US_STATES.map((s) => (
                <option key={s.fips} value={s.fips}>
                  {s.name} ({s.abbr})
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1">
            <div className="text-sm text-slate-200/70 mb-1">{t?.legalListings?.selectCounty || "County"}</div>
            <select
              className="w-full rounded-xl bg-slate-950/70 border border-sky-400/40 px-3 py-2"
              value={countyFips}
              onChange={(e) => setCountyFips(e.target.value)}
              disabled={loadingCounties || counties.length === 0}
            >
              <option value="">
                {loadingCounties
                  ? t?.legalListings?.loadingCounties || "Loading counties…"
                  : t?.legalListings?.allCounties || "All counties"}
              </option>
              {counties.map((c) => (
                <option key={c.fips} value={c.fips}>
                  {c.name}
                </option>
              ))}
            </select>
            {countyError ? <div className="mt-2 text-xs text-rose-300">{countyError}</div> : null}
            <div className="mt-2 text-xs text-slate-200/60">
              {t?.legalListings?.countyNote ||
                "County list loads via a server proxy (and falls back to the U.S. Census API). If it fails, try disabling VPN/ad-blockers or run `vercel dev` in local development."}
            </div>
          </div>
        </div>


      {/* Manual listings (client-side drafts, stored in browser localStorage) */}
      <div className="mt-5 rounded-2xl border border-sky-400/30 bg-white/5 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <div className="text-base font-semibold text-white">
                {t?.legalListings?.manualListingsTitle || "Manual listings"}
              </div>
              <span className="inline-flex items-center rounded-full bg-sky-500/15 border border-sky-400/30 px-2 py-0.5 text-xs text-sky-200/90">
                {t?.legalListings?.manualListingsBadge || "Save draft"}
              </span>
            </div>
            <div className="mt-1 text-sm text-white/70">
              {t?.legalListings?.manualListingsSub ||
                "Add a notice manually (saved in your browser). You can use this as a working area before publishing."}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onPublishCategory && onPublishCategory(mCat)}
              className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white hover:bg-white/10"
              title="Opens the publish modal for the selected category"
            >
              {t?.legalListings?.openPublish || "Open publish"}
            </button>
            <div className="text-xs text-white/50">
              {manualListings?.length ? `${manualListings.length} drafts` : "No drafts yet"}
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <label className="block">
            <div className="text-xs text-white/60">Category</div>
            <select
              className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-white outline-none"
              value={mCat}
              onChange={(e) => setMCat(e.target.value)}
            >
              {tabs.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.title}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <div className="text-xs text-white/60">Headline</div>
            <input
              className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-white outline-none"
              value={mHeadline}
              onChange={(e) => setMHeadline(e.target.value)}
              placeholder="e.g., Court Order — Hearing Notice"
            />
          </label>

          <label className="block md:col-span-2">
            <div className="text-xs text-white/60">Description</div>
            <textarea
              rows={4}
              className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-white outline-none"
              value={mDescription}
              onChange={(e) => setMDescription(e.target.value)}
              placeholder="Key details: dates, parties, filing/case info, timeline, how to respond..."
            />
          </label>

          {mCat === "traffic" && (
            <>
              <label className="block">
                <div className="text-xs text-white/60">Traffic citation # (optional)</div>
                <input
                  className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-white outline-none"
                  value={mTrafficCitation}
                  onChange={(e) => setMTrafficCitation(e.target.value)}
                  placeholder="e.g., AAWUS0E"
                />
              </label>
              <label className="block">
                <div className="text-xs text-white/60">Court/portal link (optional)</div>
                <input
                  className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-white outline-none"
                  value={mTrafficPortal}
                  onChange={(e) => setMTrafficPortal(e.target.value)}
                  placeholder="https://..."
                />
              </label>
            </>
          )}

          <label className="block">
            <div className="text-xs text-white/60">Location (optional)</div>
            <input
              className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-white outline-none"
              value={mLocation}
              onChange={(e) => setMLocation(e.target.value)}
              placeholder="City / County / Region"
            />
          </label>

          <label className="block">
            <div className="text-xs text-white/60">Price (optional)</div>
            <input
              className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-white outline-none"
              value={mPrice}
              onChange={(e) => setMPrice(e.target.value)}
              placeholder="Example: 50, 150, 500"
            />
          </label>

          <label className="block">
            <div className="text-xs text-white/60">Contact name (optional)</div>
            <input
              className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-white outline-none"
              value={mContactName}
              onChange={(e) => setMContactName(e.target.value)}
              placeholder="Name"
            />
          </label>

          <label className="block">
            <div className="text-xs text-white/60">Contact email (optional)</div>
            <input
              className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-white outline-none"
              value={mContactEmail}
              onChange={(e) => setMContactEmail(e.target.value)}
              placeholder="Email"
            />
          </label>

          <label className="block md:col-span-2">
            <div className="text-xs text-white/60">Contact phone (optional)</div>
            <input
              className="mt-1 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-white outline-none"
              value={mContactPhone}
              onChange={(e) => setMContactPhone(e.target.value)}
              placeholder="Phone"
            />
          </label>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-white/50">
            Tip: Save drafts your team can reuse. When ready, click "Open publish" to publish the selected category.
          </div>
          <div className="flex items-center gap-2">
            {mError && <div className="text-xs text-rose-200/90">{mError}</div>}
            <button
              type="button"
              onClick={addManualListing}
              className="rounded-xl border border-sky-400/40 bg-sky-500/10 px-4 py-2 text-sm text-sky-100 hover:bg-sky-500/15"
            >
              {t?.legalListings?.manualListingsAdd || "Save draft"}
            </button>
          </div>
        </div>

        {manualListings?.length ? (
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            {manualListings.map((d) => (
              <div key={d.id} className="rounded-xl border border-white/10 bg-black/30 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-white">{d.headline}</div>
                    <div className="mt-1 text-xs text-white/60">
                      {tabs.find((x) => x.key === d.category)?.title || d.category}
                      {d.location ? ` • ${d.location}` : ""}
                      {d.createdAt ? ` • ${new Date(d.createdAt).toLocaleDateString()}` : ""}
                    </div>
                    {d.description ? (
                      <div className="mt-2 line-clamp-3 text-sm text-white/70">{d.description}</div>
                    ) : null}
                    {d.category === "traffic" && (d.trafficCitation || d.trafficPortal) ? (
                      <div className="mt-2 text-xs text-white/60">
                        {d.trafficCitation ? `Citation: ${d.trafficCitation}` : ""}
                        {d.trafficPortal ? (d.trafficCitation ? " • " : "") : ""}
                        {d.trafficPortal ? "Portal link saved" : ""}
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeManualListing(d.id)}
                    className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs text-white/80 hover:bg-white/10"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* Manual resources (client-side, stored in browser localStorage) */}
<div className="mt-6">
  <div className={`${card} p-5 text-left`}>
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <div className="text-lg font-semibold text-white">
          {t?.legalListings?.manualTitle || "Manual resources"}
          <span className="ml-2 inline-flex items-center rounded-full bg-sky-500/15 border border-sky-400/30 px-2 py-0.5 text-xs text-sky-200/90">Publish Legal Listing</span>
        </div>
        <div className="text-sm text-slate-200/70">
          {t?.legalListings?.manualSub ||
            "Add your own reference links (internal SOPs, county portals, PDFs). Saved locally in your browser."}
        </div>
      </div>
      <div className="text-xs text-slate-200/60">
        {manualLinks?.length ? `${manualLinks.length} saved` : "No saved links yet"}
      </div>
    </div>

    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
      <div className="md:col-span-1">
        <div className="text-sm text-slate-200/70 mb-1">Label</div>
        <input
          className="w-full rounded-xl bg-slate-950/70 border border-sky-400/40 px-3 py-2"
          value={manualLabel}
          onChange={(e) => setManualLabel(e.target.value)}
          placeholder="e.g., Miami-Dade Clerk (Traffic)"
        />
      </div>
      <div className="md:col-span-2">
        <div className="text-sm text-slate-200/70 mb-1">URL</div>
        <div className="flex gap-2">
          <input
            className="flex-1 rounded-xl bg-slate-950/70 border border-sky-400/40 px-3 py-2"
            value={manualUrl}
            onChange={(e) => setManualUrl(e.target.value)}
            placeholder="https://example.gov/..."
          />
          <button
            type="button"
            onClick={addManualLink}
            className={`${pillBtn} whitespace-nowrap`}
            title="Add link"
          >
            Add
          </button>
        </div>
      </div>
    </div>

    {manualLinks?.length ? (
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
        {manualLinks.map((lnk) => (
          <div
            key={lnk.id}
            className="rounded-xl border border-sky-400/20 bg-slate-950/40 px-3 py-2 flex items-center justify-between gap-3"
          >
            <a
              href={lnk.href}
              target="_blank"
              rel="noreferrer"
              className="min-w-0 flex-1"
              title={lnk.href}
            >
              <div className="text-sm text-white truncate">{lnk.label}</div>
              <div className="text-[11px] text-slate-200/60 truncate">{lnk.href}</div>
            </a>
            <button
              type="button"
              onClick={() => removeManualLink(lnk.id)}
              className="text-xs px-2 py-1 rounded-lg border border-sky-400/30 hover:bg-sky-400/10"
              title="Remove"
            >
              Remove
            </button>
          </div>
        ))}
      </div>
    ) : (
      <div className="mt-4 text-sm text-slate-200/60">
        Tip: Add links you want your team to use often (portals, instructions, county resources).
      </div>
    )}
  </div>
</div>
        <div className="border-t border-sky-400/20 pt-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-lg font-semibold text-white">
                {t?.legalListings?.dirTitle || "US-wide data sources you can connect"}
              </div>
              <div className="text-sm text-slate-200/70">
                {t?.legalListings?.dirSub ||
                  "We show official directories + public APIs. Some sources require approval or do not provide APIs (marked)."}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs text-slate-200/70 select-none">
                <input
                  type="checkbox"
                  checked={showDevLinks}
                  onChange={(e) => setShowDevLinks(e.target.checked)}
                  className="accent-sky-400"
                />
                Developer links
              </label>
              <div className="text-xs text-slate-200/60">
                {t?.legalListings?.activeLabel || "Active:"} {activeTab.title}
              </div>
            </div>
          </div>

          {/* connectable sources list */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {sourcesForCategory.map((s) => (
              <div key={s.id} className={`${card} p-4`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-white">{s.name}</div>
                    {s.note ? (
                      <div className="text-xs text-slate-200/70 mt-1">{s.note}</div>
                    ) : null}
                    <div className="text-xs text-slate-200/60 mt-2">
                      {(t?.legalListings?.coverageLabel || "Coverage")}: {s.coverage} · {(t?.legalListings?.accessLabel || "Access")}: {sourceAccess(s)}
                    </div>
                  </div>
                  {sourceBadge(s) ? (
                    <span className="text-[11px] px-2 py-1 rounded-full border border-sky-400/40 text-slate-100/80">
                      {sourceBadge(s)}
                    </span>
                  ) : null}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(sourceLinks[s.id] || []).filter((lnk) => showDevLinks || !lnk.dev).slice(0, 4).map((lnk) => (
                    <a
                      key={lnk.href}
                      href={lnk.href}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs px-3 py-1 rounded-full border border-sky-400/30 hover:bg-sky-400/10"
                    >
                      {lnk.label}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>


          <div className="text-xs text-slate-200/60">
            {t?.legalListings?.dirFootnote ||
              "Next step: implement per-provider adapters (OAuth/API keys where required) and normalize results into one feed for each state/county/category."}
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
}
